/**
 * PersonaOS Final Style Library — single source of truth for the Video Express
 * Max visual styles.
 *
 * Each style defines how the video should look, how the voiceover should sound,
 * and the verbatim "Video Prompt Suffix" that is appended to every generated
 * per-scene video prompt so the video model gets consistent motion/aesthetic
 * direction. The "Visual Style" (its opening brief held in `styleDescription` +
 * the rest in `visualStyle`), "Voiceover Style" (held in `scriptStyle`), and
 * "Video Prompt Suffix" (held in `videoPromptSuffix`) come verbatim from the
 * PersonaOS Final Style Library and are threaded into the Video Express Max Sim
 * steps (concept, strategy, storyboard, scene prompt, script writer) so the
 * chosen style visibly changes the output.
 *
 * Shared by the client (the create-page selector) and the server (resolving the
 * persisted `videoStyle` value to its full guidance at each Sim seam).
 */

export interface VideoExpressStyle {
  /** Stable persisted value stored on `videoExpressCampaigns.videoStyle`. */
  value: string;
  /** Selector display label. */
  label: string;
  /** Short selector subtitle. */
  description: string;
  /** Opening sentence of the PDF "Visual Style" — the one-line creative brief. */
  styleDescription: string;
  /** Rest of the PDF "Visual Style" — how the video should look and move.
   *  `styleDescription` + `visualStyle` together = the verbatim "Visual Style". */
  visualStyle: string;
  /** PDF "Voiceover Style" — how the narration/voiceover should sound. */
  scriptStyle: string;
  /** PDF "Video Prompt Suffix" — verbatim motion/aesthetic direction appended to
   *  every generated per-scene video prompt so the rendered clips stay on-style. */
  videoPromptSuffix: string;
  /** Fixed ElevenLabs voice ID for a MALE-presenting character in this style.
   *  The voiceover step uses this (unless the campaign has an explicit
   *  per-campaign voice override) instead of auto-casting from the headshot. */
  maleVoiceId: string;
  /** Fixed ElevenLabs voice ID for a FEMALE-presenting character in this style. */
  femaleVoiceId: string;
  /** Optional 500×500 example image shown for this style in the create-page
   *  picker. The live value is the master-managed `imageUrl` column on the
   *  `video_express_styles` row (set via the admin upload UI / R2). Provided
   *  here only for styles whose example image should be seeded into a fresh DB. */
  imageUrl?: string;
  /** Seed lifecycle status. Defaults to "active". Styles seeded as "archived"
   *  are inserted soft-archived on a fresh DB so they never appear in the
   *  create-page picker, while remaining resolvable for campaigns that already
   *  chose them (coherent guidance for retired styles). */
  status?: "active" | "archived";
}

export const VIDEO_EXPRESS_STYLES: VideoExpressStyle[] = [
  {
    value: "claymation",
    label: "Claymation",
    description: "Handcrafted clay, stop-motion",
    styleDescription: "Make a handcrafted claymation commercial.",
    visualStyle: "Everything should look made from clay.",
    scriptStyle: "Playful, conversational, and energetic.",
    videoPromptSuffix:
      "Handcrafted clay animation, tactile clay textures, stop-motion inspired movement, playful squash and stretch, continuous movement throughout the full 4-second clip, no freezing, no abrupt stops.",
    maleVoiceId: "YxDI7Xbib7D0n90xAJOh",
    femaleVoiceId: "mZ9eJvA46blwulqVew6U",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/a04f9520-1910-4fd9-b840-2948c51d1baa.webp",
  },
  {
    value: "minecraft",
    label: "Minecraft",
    description: "Blocky voxel world, playful motion",
    styleDescription:
      "Make a Minecraft-style commercial with blocky voxel characters, cube-built environments, pixel-like surfaces, and playful game-world motion.",
    visualStyle:
      "Make a Minecraft-style animated commercial. Use blocky voxel characters, cube-built environments, pixel-like surfaces, bright game-world lighting, simple expressive faces, and playful cutscene-style motion.",
    scriptStyle: "Playful, upbeat, clear, and slightly adventurous.",
    videoPromptSuffix:
      "Minecraft-style animated world, blocky voxel characters, cube-built props and environments, square heads, cubed arms, no fingers, pixel-like surfaces, bright playful lighting, simple game-like movement, item pickups, product reveals, small jumps, polished cutscene energy, no live action, no clay, no paper, no anime, no LEGO studs, no glossy realistic CGI, no captions or overlays.",
    maleVoiceId: "6U2qLcO5sLBndbYeKBNC",
    femaleVoiceId: "8JJZnADfXgKjlSkDFiBS",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/6a29de90-d03e-4e63-b587-dfe16dc6f3e3.webp",
  },
  {
    value: "origamianimation",
    label: "Origami Animation",
    description: "Folded paper, crisp handcrafted motion",
    styleDescription:
      "Make a handcrafted origami animation commercial with folded-paper characters, geometric paper environments, and crisp tactile motion.",
    visualStyle:
      "Make a handcrafted origami animated commercial. Use folded-paper characters, crisp creases, angular geometric shapes, tactile paper texture, clean handmade environments, and elegant stop-motion movement.",
    scriptStyle: "Warm, clear, charming, and polished.",
    videoPromptSuffix:
      "Origami-style folded paper animation, crisp creases, angular paper geometry, visible fold lines, clean paper texture, faceted characters and props, soft shadows, precise handmade motion, subtle stop-motion charm, no flat paper cutout, no clay, no live action, no glossy CGI, no anime, no LEGO, no captions or overlays.",
    maleVoiceId: "6U2qLcO5sLBndbYeKBNC",
    femaleVoiceId: "8JJZnADfXgKjlSkDFiBS",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/77b73917-802d-4e71-9aa7-898e14d92517.webp",
  },
  {
    value: "woolstopmotion",
    label: "Wool Stop Motion",
    description: "Soft wool puppets, cozy handmade world",
    styleDescription:
      "Make a wool stop-motion commercial with soft felt puppet characters, fuzzy handmade textures, miniature sets, and cozy tactile movement.",
    visualStyle:
      "Make a wool stop-motion commercial. Use soft felt puppet characters, fuzzy wool texture, knitted clothing, miniature handmade sets, cozy lighting, shallow depth of field, and tactile stop-motion movement.",
    scriptStyle: "Soft, cozy, heartfelt, and gently playful.",
    videoPromptSuffix:
      "Wool stop-motion animation, soft felt puppet characters, fuzzy wool fibers, knitted clothing, handmade miniature sets, tactile fabric props, cozy warm lighting, shallow depth of field, gentle puppet movement, subtle stop-motion imperfections, soft handmade charm, no clay, no paper cutout, no origami, no LEGO, no Minecraft, no glossy CGI, no anime, no captions or overlays.",
    maleVoiceId: "6U2qLcO5sLBndbYeKBNC",
    femaleVoiceId: "8JJZnADfXgKjlSkDFiBS",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/920e0d94-ea83-4118-8d71-e6d371ca2675.webp",
  },
  {
    value: "pixar",
    label: "Pixar",
    description: "Cinematic animated short",
    styleDescription: "Make a Pixar-style animated short film.",
    visualStyle:
      "Use expressive characters, cinematic environments, emotional acting, warm lighting, and realistic movement.",
    scriptStyle: "Personal, reflective, and emotionally authentic.",
    videoPromptSuffix:
      "Pixar-quality cinematic animation, expressive character acting, natural secondary motion, warm cinematic lighting, realistic physics, smooth motion arcs, continuous movement throughout the full 4-second clip, no freezing, no abrupt stops.",
    maleVoiceId: "Lxifhijfev7gbjAylADE",
    femaleVoiceId: "Cm6pIopWmRdEASlSh7eH",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/2fa460a5-6901-4ba3-9f0c-f32927da9bd3.webp",
  },
  {
    value: "simpsons",
    label: "Simpsons",
    description: "Bright cartoon comedy",
    styleDescription: "Make a Simpsons-style animated commercial.",
    visualStyle:
      "Use bright cartoon environments, exaggerated expressions, visual jokes, and fast-paced animation.",
    scriptStyle: "Funny, sarcastic, and self-aware.",
    videoPromptSuffix:
      "Simpsons-style animated comedy, exaggerated facial expressions, cartoon body language, visual gags, comedic timing, fast-paced animation, continuous action throughout the full 4-second clip, no freezing, no abrupt stops.",
    maleVoiceId: "YxDI7Xbib7D0n90xAJOh",
    femaleVoiceId: "mZ9eJvA46blwulqVew6U",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/01646ba3-5fb1-48cd-8075-f664c5806878.webp",
  },
  {
    value: "infomercial",
    label: "90s Infomercial",
    description: "Retro TV infomercial",
    styleDescription: "Make a 1990s TV infomercial.",
    visualStyle:
      "Use over-the-top demonstrations, dramatic before-and-after visuals, cheesy enthusiasm, and bold graphics.",
    scriptStyle: "High-energy, enthusiastic, and sales-driven.",
    videoPromptSuffix:
      "1990s television infomercial aesthetic, dramatic demonstrations, bold callouts, exaggerated problem-solution moments, energetic camera movement, continuous activity throughout the full 4-second clip, no freezing, no abrupt stops.",
    maleVoiceId: "W4yFKr6TgoQ8SA5Agx8g",
    femaleVoiceId: "3FG35eqwFBrDGHLRxDCa",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/540aa359-f4ce-4440-b91c-b37a0d77f10d.webp",
  },
  {
    value: "lego",
    label: "LEGO",
    description: "Brick-built world",
    styleDescription: "Make a LEGO movie-style animated commercial.",
    visualStyle:
      "Everything should exist inside a LEGO world built from authentic LEGO bricks.",
    scriptStyle: "Fun, adventurous, and family-friendly.",
    videoPromptSuffix:
      "LEGO movie-quality animation, authentic brick-built environments, realistic brick physics, creative brick transformations, rich environmental detail, continuous movement throughout the full 4-second clip, no freezing, no abrupt stops.",
    maleVoiceId: "WEur5D0FQ79yMxSexPrv",
    femaleVoiceId: "MZRzGmF49goTyd5g3sj5",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/7b3ab69c-c0d3-4474-930e-132b8ae1d55c.webp",
  },
  {
    value: "videogame",
    label: "Retro Video Game",
    description: "Retro-futuristic game",
    styleDescription: "Make a retro-futuristic video game commercial.",
    visualStyle:
      "The world should feel like a playable game with power-ups, achievements, and level progression.",
    scriptStyle: "Exciting, achievement-focused, and game-announcer inspired.",
    videoPromptSuffix:
      "Video game-inspired animation, arcade-style visual effects, power-up moments, level progression visuals, dynamic game UI elements, achievement animations, retro-futuristic game world, continuous gameplay-style motion throughout the full 4-second clip, no freezing, no abrupt stops.",
    maleVoiceId: "mHQpS3tQkgmWYGnNYGbJ",
    femaleVoiceId: "5E4eJSP4YOcnxgLsv9YW",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/98722386-c4ef-44a5-9869-0e2116f02ae7.webp",
  },
  // ---- Archived styles ----------------------------------------------------
  // Kept in the library (soft-archived) so campaigns that already chose them
  // still resolve to coherent guidance, but excluded from the create-page
  // picker. Seeded with status "archived" so a fresh DB never shows them.
  {
    value: "anime",
    label: "Anime",
    description: "Cinematic anime action",
    styleDescription: "Make a modern anime-style animated commercial.",
    visualStyle:
      "Use dramatic camera angles, expressive characters, stylized motion effects, and cinematic action framing.",
    scriptStyle: "Passionate, emotional, and energetic.",
    videoPromptSuffix:
      "Premium anime-quality animation, dynamic camera movement, expressive character reactions, dramatic lighting, stylized motion effects, cinematic action framing, fluid character animation, continuous movement throughout the full 4-second clip, no freezing, no abrupt stops.",
    maleVoiceId: "kkuawwVTMNS4rym4yrX5",
    femaleVoiceId: "BEFglTK3ryCc0V3teV7b",
    status: "archived",
  },
  {
    value: "comicbook",
    label: "Comic Book",
    description: "Graphic novel panels",
    styleDescription: "Make a comic book-style animated commercial.",
    visualStyle:
      "Use bold outlines, graphic novel shading, halftone textures, action lines, and dynamic comic panels.",
    scriptStyle: "Bold, dramatic, and larger-than-life.",
    videoPromptSuffix:
      "Comic book-style animation, bold outlines, graphic novel shading, halftone textures, dynamic action lines, dramatic poses, stylized panel transitions, living comic book aesthetic, continuous motion throughout the full 4-second clip, no freezing, no abrupt stops.",
    maleVoiceId: "waxNe55qNf0eB1Bh5K7C",
    femaleVoiceId: "wedoqwDhipIdsG60BJaL",
    imageUrl:
      "https://pub-4e43112aee73445b8d42573b9566dd62.r2.dev/video-express-styles/04100196-7bfc-4bff-9f1c-3fd83939f5d5.webp",
    status: "archived",
  },
];

export const DEFAULT_VIDEO_EXPRESS_STYLE: VideoExpressStyle = VIDEO_EXPRESS_STYLES[0];

/** Resolve a persisted `videoStyle` value to its full library entry. Falls back
 *  to the default (Claymation) for unknown/empty values so the pipeline always
 *  has coherent style guidance. */
export function getVideoExpressStyle(value?: string | null): VideoExpressStyle {
  if (!value) return DEFAULT_VIDEO_EXPRESS_STYLE;
  const v = String(value).trim().toLowerCase();
  return VIDEO_EXPRESS_STYLES.find((s) => s.value === v) || DEFAULT_VIDEO_EXPRESS_STYLE;
}

/** Combined visual-direction instruction (description + visual style) fed to the
 *  image/video-facing Sim seams: concept, strategy, storyboard, scene prompt. */
export function videoExpressVisualStyleInstruction(value?: string | null): string {
  const s = getVideoExpressStyle(value);
  return `${s.styleDescription} ${s.visualStyle}`.trim();
}

/** Voiceover-style instruction (how the narration should sound) fed to the
 *  narration/script writer. */
export function videoExpressScriptStyleInstruction(value?: string | null): string {
  return getVideoExpressStyle(value).scriptStyle;
}

/** Normalize a free-text gender value (e.g. "man", "Woman", "Male", "F") to the
 *  canonical "male"/"female", or null when it can't be determined. Checks the
 *  female forms first because "female" contains "male" and "woman" contains
 *  "man". */
export function normalizeGender(value?: string | null): "male" | "female" | null {
  const g = (value || "").trim().toLowerCase();
  if (!g) return null;
  if (
    g === "f" ||
    g.includes("female") ||
    g.includes("woman") ||
    g.includes("women") ||
    g.includes("girl") ||
    g.includes("feminine")
  ) {
    return "female";
  }
  if (
    g === "m" ||
    g.includes("male") ||
    g.includes("man") ||
    g.includes("men") ||
    g.includes("boy") ||
    g.includes("masculine")
  ) {
    return "male";
  }
  return null;
}

/** The fixed ElevenLabs voice ID for a persisted `videoStyle` value, picked by
 *  the character's gender. Returns the female voice for a female-presenting
 *  character and the male voice otherwise — the male voice is also the
 *  deterministic default when the gender is unspecified/indeterminate, so this
 *  always resolves to a concrete style voice. */
export function videoExpressStyleVoiceId(value?: string | null, gender?: string | null): string | null {
  const s = getVideoExpressStyle(value);
  return normalizeGender(gender) === "female"
    ? s.femaleVoiceId || null
    : s.maleVoiceId || null;
}

/** The verbatim "Video Prompt Suffix" for a persisted `videoStyle` value. This
 *  is appended to every generated per-scene video prompt at the scene-prompts
 *  stage so the rendered clips carry the chosen style's motion/aesthetic cues. */
export function videoExpressVideoPromptSuffix(value?: string | null): string {
  return getVideoExpressStyle(value).videoPromptSuffix;
}

/** Append the style's "Video Prompt Suffix" to a generated scene prompt.
 *  Idempotent: it won't double-append if the prompt already ends with the
 *  suffix (so re-edits/regenerations stay clean). Joins with sentence-ending
 *  punctuation when the base prompt has none. */
export function appendVideoPromptSuffix(prompt: string, value?: string | null): string {
  const base = (prompt || "").trim();
  const suffix = videoExpressVideoPromptSuffix(value).trim();
  if (!suffix) return base;
  if (!base) return suffix;
  if (base.endsWith(suffix)) return base;
  const sep = /[.!?]$/.test(base) ? " " : ". ";
  return `${base}${sep}${suffix}`;
}
